import { refreshCanvasSource } from './interpol.js';
import {
    getScenarioHorizon,
    scenarioFrameForTime,
    territorialScenarioForTime,
} from '../lib/intelligence-scenario.js';
import {
    eventTrustLabel,
    newerDiagnosisSnapshot,
} from '../lib/weather-briefing.js';
import { mountIntelligenceWorkspace, updateIntelligenceWorkspaceContext } from '../ui/intelligence-workspace.js';

// WS Monitor dentro Weather Sicily Maps: Maps resta l'unica mappa; questo
// modulo aggiunge Total Fusion, barometria europea, masse d'aria e traiettorie.
const STATE_URL = '/api/state.php?resource=state';
const WSNOW_V3_URL = '/api/wsnow-v3.php';
const REFRESH_MS = 5 * 60 * 1000;
const WIND_CONTRACT_ID = 'ws-wind-10m-kmh-v1';
const PARTICLE_MOTION_CONTRACT_ID = 'ws-wind-particle-linear-kmh-v1';
const IDS = { t850:'wsi-t850', pressure:'wsi-pressure-field', pressureSrc:'wsi-pressure-field-src', isobar:'wsi-isobar-labels', isobarSrc:'wsi-isobar-labels-src', isobarVector:'wsi-isobar-vector', isobarVectorSrc:'wsi-isobar-vector-src', wind:'wsi-wind-particles', windSrc:'wsi-wind-particles-src', advancedSrc:'wsi-advanced-src', advancedFill:'wsi-advanced-fill', advancedLine:'wsi-advanced-line', territorialSrc:'wsi-territorial-src', territorialPoint:'wsi-territorial-point', territorialTrack:'wsi-territorial-track', physics:'wsi-physics-field', physicsSrc:'wsi-physics-field-src', stream:'wsi-stream', past:'wsi-track-past', future:'wsi-track-future' };

let map = null;
let state = null;
let wsnowV3 = null;
let panel = null;
let launchButton = null;
let enabled = true;
let markers = [];
let featureMarkers = new Map();
let stepIndex = 0;
let pressureCanvas = null;
let isobarCanvas = null;
let currentGrid = null;
let gridRequest = 0;
let userLocation = null;
let userMarker = null;
const gridCache = new Map();
let windCanvas = null;
let windParticles = [];
let windRaf = 0;
let windActive = false;
let windLastFrame = 0;
let windBounds = null;
let advancedRequest = 0;
let advancedPopup = null;
let territorialPopup = null;
let territorialGeoJSON = null;
let territorialDocument = null;
let physicsCanvas = null;
let physicsRequest = 0;
let currentPhysics = null;
let publicationCycle = '';
let unregisterScenarioProvider = null;
let workspacePanelApi = null;
let legacyParamDockWasCollapsed = null;
let selectedScenarioTimeMs = null;
let pendingScenarioTimeMs = null;
let scenarioApplySequence = 0;
let liveOnlyLayerSnapshot = null;
const LIVE_ONLY_TOGGLE_IDS = ['chkRadar','chkHail','chkSatellite','chkLightning','chkTemporali'];
// La vista iniziale e' una sinottica, non una dashboard sovrapposta alla mappa:
// fondo pulito + isobare + scie. Il campo colore resta disponibile su richiesta.
let shadeMode = 'off';
const visible = { isobars:false, wind:true, advanced:false, territorial:false, centers:true, tracks:false, insights:false };
let cleanDefaultsApplied = false;
let uiMode = 'wind';
const WIND_SIMULATION_HOURS_PER_SECOND = 1.45;

function activeHere() {
    const q = new URLSearchParams(location.search);
    return location.hostname.startsWith('intelligence.') || q.get('wsmonitor') === '1';
}

function esc(v) {
    return String(v ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
}

function cycleAssetUrl(path) {
    const logical=String(path||'').replace(/^\/+/, '');
    if(!publicationCycle) return '/'+logical;
    const query=new URLSearchParams({cycle:publicationCycle,path:logical});
    return '/api/cycle-asset.php?'+query.toString();
}

function syncWorkspaceContract() {
    const workspace=window.WSWorkspace;if(!workspace||!state)return;
    const scenario=state.synoptic_scenario||{},steps=scenario.timesteps||[];
    workspace.store.setCycle({id:publicationCycle||null,generatedAtUtc:state.generated_at_utc||null});
    workspace.store.setExtension('intelligence',{status:state.status||null,sourceCount:state.live?.total_fusion?.source_count||0,freshSourceCount:state.live?.total_fusion?.fresh_source_count||0,scenarioStatus:scenario.status||'unavailable',scenarioSteps:steps.length,visualObservations:state.visual_observations?.usable_observation_count||0});
    unregisterScenarioProvider?.();
    unregisterScenarioProvider=workspace.time.registerProvider({
        id:'intelligence-scenario',toleranceMs:90*60*1000,
        coverage:()=>({timestamps:steps.map(step=>step.valid_utc),toleranceMs:90*60*1000}),
    });
}

function workspaceTemporalMode(targetMs) {
    const nowMs = Number(getScenarioHorizon(state)?.now_ms) || Date.now();
    const deltaMs = Number(targetMs) - nowMs;
    if (!Number.isFinite(deltaMs) || Math.abs(deltaMs) <= 5 * 60 * 1000) return 'live';
    if (deltaMs > 0 && deltaMs <= 90 * 60 * 1000) return 'nowcast';
    return deltaMs > 0 ? 'forecast' : 'history';
}

function requestWorkspaceTimeline(intent) {
    const explicitTarget = Number(intent?.targetMs);
    if (Number.isFinite(explicitTarget)) {
        document.dispatchEvent(new CustomEvent('wsv2:intelligence-time-request', {
            detail: {T: explicitTarget},
        }));
        return;
    }
    const horizon = getScenarioHorizon(state);
    const nowMs = Number(horizon?.now_ms) || Date.now();
    const steps = (state?.synoptic_scenario?.timesteps || [])
        .map(step => Date.parse(step.valid_utc)).filter(Number.isFinite).sort((a, b) => a - b);
    let targetMs = nowMs;
    if (intent === 'nowcast') targetMs = nowMs + 30 * 60 * 1000;
    else if (intent === 'forecast') targetMs = nowMs + 3 * 60 * 60 * 1000;
    else if (intent === 'scenario') {
        targetMs = steps.find(time => time > (selectedScenarioTimeMs || nowMs))
            || steps.find(time => time > nowMs) || nowMs;
    }
    document.dispatchEvent(new CustomEvent('wsv2:intelligence-time-request', {detail: {T: targetMs}}));
}

function rememberWorkspaceSection(section) {
    const allowed = new Set(['summary', 'live', 'forecast', 'tracking', 'system']);
    if (allowed.has(String(section))) window.WSWorkspace?.store?.setSection?.(String(section));
}

function color(sev) {
    return { critical:'#ff355d', warning:'#ff7b35', watch:'#ffc83d', info:'#3ba5ff' }[sev] || '#8ea0b8';
}

function wsnowV3Status() {
    const current=wsnowV3?.authoritative||null;
    if (!current) {
        return {
            operational:false,
            status:'NON DISPONIBILE',
            description:'Lo stato operativo V3 non è disponibile; nessun altro motore viene usato come fallback.',
            kpis:[],
            footer:'',
        };
    }
    const operational=current.product==='weather-sicily-now-v3'
        && current.mode==='production' && current.authoritative===true && current.stale===false;
    const generated=current.generated_at_utc
        ? new Date(current.generated_at_utc).toLocaleTimeString('it-IT',{hour:'2-digit',minute:'2-digit'})
        : 'n/d';
    const age=Number(current.age_minutes);
    return {
        operational,
        status:operational?'OPERATIVO':'BLOCCATO',
        description:'Unico motore operativo per radar, celle temporalesche e notifiche di pioggia in arrivo.',
        kpis:[
            {value:operational?'LIVE':'STOP',label:'autorità'},
            {value:Number(current.ensemble_members)||0,label:'membri'},
            {value:Number(current.forecast_frame_count)||0,label:'lead'},
            {value:Number.isFinite(age)?age.toFixed(1):'n/d',label:'minuti'},
            {value:current.notification_dispatch_eligible===true?'SÌ':'NO',label:'notifiche'},
        ],
        footer:`Ciclo ${current.cycle_id||'n/d'} · ${generated} · authority: WS Now V3`,
    };
}

function setImageVisible(id, visible) {
    if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
}
function setIsobarsVisible(visible){setImageVisible(IDS.isobar,visible);setImageVisible(IDS.isobarVector,visible);}
function setTracksVisible(visible){setImageVisible(IDS.past,visible);setImageVisible(IDS.future,visible);}

function mercatorY(lat) {
    return Math.log(Math.tan(Math.PI / 4 + lat * Math.PI / 360));
}

function pressureColor(p) {
    // Rampa barica volutamente sobria: blu = pressione bassa, grigio chiaro =
    // atmosfera standard, ocra/rosa spento = pressione alta. Non e' una scala
    // di pericolo e non deve sembrare temperatura, radar o incendio.
    const stops=[[970,[38,83,126]],[985,[57,117,158]],[1000,[91,151,177]],
        [1007,[139,177,190]],[1013,[190,202,198]],[1020,[204,191,164]],
        [1030,[190,153,125]],[1040,[169,123,112]],[1050,[142,101,108]]];
    let i=1; while(i<stops.length && p>stops[i][0]) i++;
    const a=stops[Math.max(0,i-1)], b=stops[Math.min(stops.length-1,i)];
    const t=a[0]===b[0]?0:Math.max(0,Math.min(1,(p-a[0])/(b[0]-a[0])));
    return a[1].map((v,k)=>Math.round(v+(b[1][k]-v)*t));
}

function pressureAlpha(p) {
    // Il fondo resta leggibile vicino a 1013 hPa; gli estremi sono un poco piu'
    // presenti, senza trasformare la mappa in una heatmap aggressiva.
    return Math.round(112 + 72 * Math.min(1,Math.abs(p-1013)/35));
}

function windSpeedColor(speed) {
    // Rampa ispirata alle mappe di vento operative: calma blu-grigia, brezze
    // ciano, vento moderato verde, valori forti ocra/viola. Il rosso non viene
    // usato, quindi la mappa non comunica falsamente incendio o allerta.
    const stops=[[0,[211,235,242]],[8,[163,215,226]],[16,[101,191,205]],
        [28,[70,166,161]],[40,[92,157,112]],[55,[183,172,91]],
        [70,[190,126,91]],[90,[147,89,126]],[120,[103,73,132]]];
    let i=1;while(i<stops.length&&speed>stops[i][0])i++;
    const a=stops[Math.max(0,i-1)],b=stops[Math.min(stops.length-1,i)];
    const t=a[0]===b[0]?0:Math.max(0,Math.min(1,(speed-a[0])/(b[0]-a[0])));
    return a[1].map((v,k)=>Math.round(v+(b[1][k]-v)*t));
}

function temperature850Color(value) {
    // Temperatura della massa d'aria a circa 1.500 m: rampa divergente con
    // centro chiaro attorno a 10 °C, distinta dai colori di allerta.
    const stops=[[-24,[77,91,167]],[-12,[82,146,198]],[0,[126,191,205]],
        [8,[216,230,215]],[14,[242,221,159]],[20,[224,164,102]],
        [28,[190,103,83]],[36,[135,72,100]]];
    let i=1;while(i<stops.length&&value>stops[i][0])i++;
    const a=stops[Math.max(0,i-1)],b=stops[Math.min(stops.length-1,i)];
    const t=a[0]===b[0]?0:Math.max(0,Math.min(1,(value-a[0])/(b[0]-a[0])));
    return a[1].map((v,k)=>Math.round(v+(b[1][k]-v)*t));
}

function updateShadeLegend() {
    const legend=panel?.querySelector('.wsi-pressure-legend');if(!legend)return;
    const title=legend.querySelector('b'),unit=legend.querySelector('small'),bar=legend.querySelector(':scope>i');
    const ticks=legend.querySelector('.wsi-pressure-legend__ticks'),note=legend.querySelector('p');
    if(shadeMode==='wind'){
        title.textContent='Velocità vento a 10 m';unit.textContent='ECMWF · km/h';
        bar.style.background='linear-gradient(90deg,#d3ebf2,#a3d7e2,#65bfcd,#46a6a1,#5c9d70,#b7ac5b,#be7e5b,#93597e,#674984)';
        ticks.innerHTML='<span>0 · calma</span><span>40 · sostenuto</span><span>90+ · molto forte</span>';
        note.textContent='Colore = velocità · scie chiare = direzione e moto';
    }else if(shadeMode==='t850'){
        title.textContent='Massa d’aria a 850 hPa';unit.textContent='ECMWF · °C';
        bar.style.background='linear-gradient(90deg,#4d5ba7,#5292c6,#7ebfcd,#d8e6d7,#f2dd9f,#e0a466,#be6753,#874864)';
        ticks.innerHTML='<span>−12 · fredda</span><span>10 · mite</span><span>28+ · molto calda</span>';
        note.textContent='Temperatura prevista a circa 1.500 m · cambia con ogni scadenza';
    }else{
        title.textContent='Campo barico MSLP';unit.textContent='ECMWF · hPa';
        bar.style.background='linear-gradient(90deg,#26537e,#39759e,#5b97b1,#8bb1be,#becac6,#ccbfa4,#be997d,#a97b70,#8e656c)';
        ticks.innerHTML='<span>970 · bassa</span><span>1013 · neutra</span><span>1050 · alta</span>';
        note.textContent='Colore = pressione · scie chiare = vento a 10 m';
    }
}

function ensureBaricLayers() {
    if (pressureCanvas) return;
    const [[south,west],[north,east]]=state.overlays.bounds;
    const width=2200, height=Math.round(width*(mercatorY(north)-mercatorY(south))/((east-west)*Math.PI/180));
    pressureCanvas=document.createElement('canvas'); pressureCanvas.width=width; pressureCanvas.height=height;
    isobarCanvas=document.createElement('canvas'); isobarCanvas.width=width; isobarCanvas.height=height;
    const coordinates=[[west,north],[east,north],[east,south],[west,south]];
    const before=map.getLayer('ws-stations-symbols')?'ws-stations-symbols':undefined;
    map.addSource(IDS.pressureSrc,{type:'canvas',canvas:pressureCanvas,coordinates,animate:false});
    map.addLayer({id:IDS.pressure,type:'raster',source:IDS.pressureSrc,paint:{'raster-opacity':.38,'raster-fade-duration':180,'raster-resampling':'linear'}},before);
    map.addSource(IDS.isobarSrc,{type:'canvas',canvas:isobarCanvas,coordinates,animate:false});
    map.addLayer({id:IDS.isobar,type:'raster',source:IDS.isobarSrc,paint:{'raster-opacity':.94,'raster-fade-duration':0}},before);
    map.addSource(IDS.isobarVectorSrc,{type:'geojson',data:{type:'FeatureCollection',features:[]}});
    map.addLayer({id:IDS.isobarVector,type:'line',source:IDS.isobarVectorSrc,paint:{
        'line-color':['case',['==',['get','level'],1012],'#ffe778','#ffffff'],
        'line-width':['interpolate',['linear'],['zoom'],4,.85,8,1.35,12,2.1,16,2.8],
        'line-opacity':.9,'line-blur':.15,
    }},before);
}

function renderPressureGrid(grid) {
    ensureBaricLayers();
    const {nx,ny}=grid, W=pressureCanvas.width, H=pressureCanvas.height;
    const raw=document.createElement('canvas'); raw.width=nx; raw.height=ny;
    const rctx=raw.getContext('2d'), img=rctx.createImageData(nx,ny);
    for(let i=0;i<grid.mslp.length;i++){
        const speed=Math.hypot(Number(grid.u10?.[i]||0),Number(grid.v10?.[i]||0));
        const t850=Number(grid.t850?.[i]);
        const c=shadeMode==='wind'?windSpeedColor(speed):shadeMode==='t850'&&Number.isFinite(t850)?temperature850Color(t850):pressureColor(grid.mslp[i]); const o=i*4;
        img.data[o]=c[0];img.data[o+1]=c[1];img.data[o+2]=c[2];
        img.data[o+3]=shadeMode==='wind'?Math.round(130+60*Math.min(1,speed/70)):shadeMode==='t850'?182:pressureAlpha(grid.mslp[i]);
    }
    rctx.putImageData(img,0,0);
    const ctx=pressureCanvas.getContext('2d'); ctx.clearRect(0,0,W,H); ctx.imageSmoothingEnabled=true; ctx.imageSmoothingQuality='high';
    const yTop=mercatorY(grid.lat_top), yBottom=mercatorY(grid.lat_bottom);
    for(let y=0;y<H;y++){
        const my=yTop+(yBottom-yTop)*(y/(H-1));
        const lat=(2*Math.atan(Math.exp(my))-Math.PI/2)*180/Math.PI;
        const sy=(grid.lat_top-lat)/(grid.lat_top-grid.lat_bottom)*(ny-1);
        const y0=Math.max(0,Math.min(ny-1,Math.floor(sy))), y1=Math.min(ny-1,y0+1), f=sy-y0;
        ctx.globalAlpha=1-f;ctx.drawImage(raw,0,y0,nx,1,0,y,W,1);
        if(f>0){ctx.globalAlpha=f;ctx.drawImage(raw,0,y1,nx,1,0,y,W,1);}
    }
    ctx.globalAlpha=1;

    const ic=isobarCanvas.getContext('2d'); ic.clearRect(0,0,W,H); ic.lineJoin='round';ic.lineCap='round';
    const vals=grid.mslp;let min=Infinity,max=-Infinity;for(const v of vals){if(v<min)min=v;if(v>max)max=v;}const step=2;
    const px=x=>x/(nx-1)*W;
    const py=y=>{const lat=grid.lat_top-(grid.lat_top-grid.lat_bottom)*(y/(ny-1));return (mercatorY(grid.lat_top)-mercatorY(lat))/(mercatorY(grid.lat_top)-mercatorY(grid.lat_bottom))*H;};
    const edge=(x1,y1,v1,x2,y2,v2,L)=>{const t=(L-v1)/(v2-v1);return [px(x1+(x2-x1)*t),py(y1+(y2-y1)*t)];};
    const toGeo=p=>{const lon=grid.lon_left+p[0]/W*(grid.lon_right-grid.lon_left),my=mercatorY(grid.lat_top)-p[1]/H*(mercatorY(grid.lat_top)-mercatorY(grid.lat_bottom));return [lon,(2*Math.atan(Math.exp(my))-Math.PI/2)*180/Math.PI];};
    const contourFeatures=[];
    for(let L=Math.ceil(min/step)*step;L<=max;L+=step){
        // Evita che valori quantizzati identici al livello (es. 1014.00)
        // seguano per chilometri il bordo delle celle creando falsi rettangoli.
        const C=L+.001;
        let segments=0,label=null;const lines=[];
        for(let y=0;y<ny-1;y++)for(let x=0;x<nx-1;x++){
            const i=y*nx+x,v00=vals[i],v10=vals[i+1],v11=vals[i+nx+1],v01=vals[i+nx],pts=[];
            if((v00<C)!=(v10<C))pts.push(edge(x,y,v00,x+1,y,v10,C));
            if((v10<C)!=(v11<C))pts.push(edge(x+1,y,v10,x+1,y+1,v11,C));
            if((v11<C)!=(v01<C))pts.push(edge(x+1,y+1,v11,x,y+1,v01,C));
            if((v01<C)!=(v00<C))pts.push(edge(x,y+1,v01,x,y,v00,C));
            for(let k=0;k+1<pts.length;k+=2){lines.push([toGeo(pts[k]),toGeo(pts[k+1])]);segments++;if(!label&&segments>120&&x%18===0)label=pts[k];}
        }
        if(lines.length)contourFeatures.push({type:'Feature',properties:{level:L},geometry:{type:'MultiLineString',coordinates:lines}});
        if(label){ic.font='700 12px sans-serif';ic.textAlign='center';ic.textBaseline='middle';ic.lineWidth=3;ic.strokeStyle='rgba(7,15,28,.9)';ic.strokeText(String(L),label[0],label[1]);ic.fillStyle='#fff';ic.fillText(String(L),label[0],label[1]);}
    }
    map.getSource(IDS.isobarVectorSrc)?.setData({type:'FeatureCollection',features:contourFeatures});
    refreshCanvasSource(map,IDS.pressureSrc);refreshCanvasSource(map,IDS.isobarSrc);
    updateShadeLegend();
}

function windCanvasPoint(lat,lon) {
    const b=windBounds,W=windCanvas.width,H=windCanvas.height;
    return {x:(lon-b.w)/(b.e-b.w)*W,
        y:(mercatorY(b.n)-mercatorY(lat))/(mercatorY(b.n)-mercatorY(b.s))*H};
}

function advectWindPoint(lat,lon,uKmh,vKmh,dtSeconds) {
    const hours=dtSeconds*WIND_SIMULATION_HOURS_PER_SECOND;
    const cos=Math.max(.2,Math.cos(lat*Math.PI/180));
    return {lat:lat+(vKmh*hours/111.32),lon:lon+(uKmh*hours/(111.32*cos))};
}

// Kernel unico del prodotto attivo: scie, popup barico e ricevuta Chromium
// devono campionare esattamente gli stessi valori, senza una replica parallela.
function sampleGridArray(grid,values,lat,lon) {
    if(!grid||!values||lat>grid.lat_top||lat<grid.lat_bottom||lon<grid.lon_left||lon>grid.lon_right)return null;
    if(values.length!==Number(grid.nx)*Number(grid.ny))return null;
    const x=(lon-grid.lon_left)/(grid.lon_right-grid.lon_left)*(grid.nx-1),y=(grid.lat_top-lat)/(grid.lat_top-grid.lat_bottom)*(grid.ny-1);
    const x0=Math.floor(x),y0=Math.floor(y),x1=Math.min(grid.nx-1,x0+1),y1=Math.min(grid.ny-1,y0+1),fx=x-x0,fy=y-y0;
    const a=values[y0*grid.nx+x0]*(1-fx)+values[y0*grid.nx+x1]*fx;
    const b=values[y1*grid.nx+x0]*(1-fx)+values[y1*grid.nx+x1]*fx;
    return a*(1-fy)+b*fy;
}

function fieldAt(grid,key,lat,lon) {
    return sampleGridArray(grid,grid?.[key],lat,lon);
}

function windAnimationSelfTest() {
    const slow=advectWindPoint(37,14,5,0,1);
    const fast=advectWindPoint(37,14,20,0,1);
    const calm=advectWindPoint(37,14,0,0,1);
    const ratio=Math.abs((fast.lon-14)/(slow.lon-14));
    const calmDisplacement=Math.hypot(calm.lat-37,calm.lon-14);
    return Object.freeze({
        contract:PARTICLE_MOTION_CONTRACT_ID,
        pass:Math.abs(ratio-4)<1e-12&&calmDisplacement===0,
        speed_ratio_input:4,
        displacement_ratio:ratio,
        calm_displacement:calmDisplacement,
        simulated_hours_per_real_second:WIND_SIMULATION_HOURS_PER_SECOND,
    });
}

// Contratto runtime usato per verificare unità e scala dell'animazione.
window.WSIntelligenceDisplayContract=Object.freeze({
    id:'ws-intelligence-v2-display-v1',
    renderer:'maplibre-canvas-wind-v1',
    wind_contract_id:WIND_CONTRACT_ID,
    particle_motion_contract:PARTICLE_MOTION_CONTRACT_ID,
    sampleGrid:sampleGridArray,
    animationSelfTest:windAnimationSelfTest,
});

function ensureWindLayer() {
    if(windCanvas)return true;
    syncWindViewport(true);
    if(!windCanvas||!windBounds)return false;
    const {w,e,s,n}=windBounds;
    const coordinates=[[w,n],[e,n],[e,s],[w,s]];
    const before=map.getLayer('ws-stations-symbols')?'ws-stations-symbols':undefined;
    map.addSource(IDS.windSrc,{type:'canvas',canvas:windCanvas,coordinates,animate:true});
    map.addLayer({id:IDS.wind,type:'raster',source:IDS.windSrc,paint:{'raster-opacity':.86,'raster-fade-duration':0}},before);
    map.setLayoutProperty(IDS.wind,'visibility','none');
    map.on('moveend',()=>{if(windActive){syncWindViewport();seedWindParticles();}});
    return true;
}

function syncWindViewport(create=false) {
    if(!currentGrid)return;
    const mb=map.getBounds(),g=currentGrid;
    const w=Math.max(g.lon_left,mb.getWest()),e=Math.min(g.lon_right,mb.getEast());
    const s=Math.max(g.lat_bottom,mb.getSouth()),n=Math.min(g.lat_top,mb.getNorth());
    if(!(w<e&&s<n))return;
    windBounds={w,e,s,n};
    if(create)windCanvas=document.createElement('canvas');
    if(!windCanvas)return;
    const rect=map.getCanvas().getBoundingClientRect();
    const dpr=Math.min(2,window.devicePixelRatio||1);
    const width=Math.max(640,Math.min(2200,Math.round(rect.width*dpr)));
    const height=Math.max(420,Math.min(1600,Math.round(rect.height*dpr)));
    if(windCanvas.width!==width)windCanvas.width=width;
    if(windCanvas.height!==height)windCanvas.height=height;
    map.getSource(IDS.windSrc)?.setCoordinates([[w,n],[e,n],[e,s],[w,s]]);
}

function resetWindParticle(p,bounds) {
    p.lon=bounds.w+Math.random()*(bounds.e-bounds.w);p.lat=bounds.s+Math.random()*(bounds.n-bounds.s);
    // Tracce abbastanza lunghe da mostrare il moto, ma non tanto da accumularsi
    // artificialmente sulle linee di convergenza del modello.
    p.age=18+Math.floor(Math.random()*25);return p;
}

function windSeedBounds() {
    return windBounds;
}

function seedWindParticles() {
    if(!currentGrid||!windCanvas)return;
    const b=windSeedBounds();if(!b||b.w>=b.e||b.s>=b.n){windParticles=[];return;}
    const pixels=windCanvas.width*windCanvas.height;
    const count=Math.round(Math.max(550,Math.min(1500,pixels/1250)));
    windParticles=Array.from({length:count},()=>resetWindParticle({},b));
    windCanvas.getContext('2d').clearRect(0,0,windCanvas.width,windCanvas.height);
}

function drawWindFrame(now) {
    if(!windActive||!currentGrid||!windCanvas){windRaf=0;return;}
    windRaf=requestAnimationFrame(drawWindFrame);
    if(now-windLastFrame<34)return;
    const dt=Math.min(.08,(now-windLastFrame)/1000||.034);windLastFrame=now;
    const ctx=windCanvas.getContext('2d'),bounds=windSeedBounds();
    ctx.save();ctx.globalCompositeOperation='destination-out';ctx.fillStyle='rgba(0,0,0,.085)';ctx.fillRect(0,0,windCanvas.width,windCanvas.height);ctx.restore();
    const dpr=Math.min(2,window.devicePixelRatio||1);
    ctx.save();ctx.globalCompositeOperation='source-over';ctx.lineWidth=1.05*dpr;ctx.lineCap='round';
    // I payload field-grid esportano u/v in km/h (ECMWF li converte una sola
    // volta a monte). 1,45 h simulate per secondo reale mantiene un moto fluido
    // senza il precedente errore x3,6 che esasperava convergenze e orografia.
    for(const p of windParticles){
        if(p.age--<=0||p.lon<bounds.w||p.lon>bounds.e||p.lat<bounds.s||p.lat>bounds.n){resetWindParticle(p,bounds);continue;}
        const u=fieldAt(currentGrid,'u10',p.lat,p.lon),v=fieldAt(currentGrid,'v10',p.lat,p.lon);if(u==null||v==null){resetWindParticle(p,bounds);continue;}
        const a=windCanvasPoint(p.lat,p.lon),next=advectWindPoint(p.lat,p.lon,u,v,dt),nlat=next.lat,nlon=next.lon;
        const z=windCanvasPoint(nlat,nlon),speed=Math.hypot(u,v);
        // Le scie descrivono soltanto il moto: nessun giallo/rosso da "fuoco".
        // Intensita' e luminosita' aumentano col vento, la tinta resta neutra.
        ctx.strokeStyle=speed<10?'rgba(235,247,252,.42)':speed<30?'rgba(255,255,255,.72)':speed<55?'rgba(205,238,250,.86)':'rgba(126,211,246,.96)';
        ctx.beginPath();ctx.moveTo(a.x,a.y);ctx.lineTo(z.x,z.y);ctx.stroke();p.lat=nlat;p.lon=nlon;
    }
    ctx.restore();
}

function setWindAnimated(on) {
    windActive=!!on;ensureWindLayer();setImageVisible(IDS.wind,windActive);setImageVisible(IDS.stream,false);
    if(windActive){syncWindViewport();seedWindParticles();windLastFrame=performance.now();if(!windRaf)windRaf=requestAnimationFrame(drawWindFrame);}
    else {if(windRaf)cancelAnimationFrame(windRaf);windRaf=0;windCanvas?.getContext('2d').clearRect(0,0,windCanvas.width,windCanvas.height);}
}

function ensureAdvancedLayers() {
    if(map.getSource(IDS.advancedSrc))return;
    const before=map.getLayer('ws-stations-symbols')?'ws-stations-symbols':undefined;
    map.addSource(IDS.advancedSrc,{type:'geojson',data:{type:'FeatureCollection',features:[]}});
    map.addLayer({id:IDS.advancedFill,type:'fill',source:IDS.advancedSrc,filter:['==',['geometry-type'],'Polygon'],paint:{
        'fill-color':['match',['get','kind'],'warm_advection','#ff6b35','cold_advection','#3195ff','jet_streak','#a65cff','surface_convergence','#27c879','pressure_fall','#e83e55','pressure_rise','#3f7ee8','#9aa8b8'],
        'fill-opacity':['match',['get','kind'],'jet_streak',.20,'surface_convergence',.15,.12],
        'fill-outline-color':['match',['get','kind'],'warm_advection','#ff8a54','cold_advection','#5eb0ff','jet_streak','#c28aff','surface_convergence','#55dfa0','#aab6c5'],
    }},before);
    map.addLayer({id:IDS.advancedLine,type:'line',source:IDS.advancedSrc,filter:['==',['geometry-type'],'LineString'],paint:{
        'line-color':['match',['get','front_type'],'cold_front','#318cff','warm_front','#ff4f57','baroclinic_boundary','#bd68ff',['match',['get','kind'],'pressure_gradient','#ffd65a','#ffffff']],
        'line-width':['interpolate',['linear'],['zoom'],4,1.5,9,2.7,14,4],
        'line-opacity':.9,'line-dasharray':['case',['==',['get','kind'],'pressure_gradient'],['literal',[2,2]],['literal',[1,0]]],
    }},before);
    const popup=e=>{const f=e.features?.[0],p=f?.properties;if(!p)return;advancedPopup?.remove();advancedPopup=new maplibregl.Popup({className:'wsi-popup-shell',maxWidth:'360px',offset:10}).setLngLat(e.lngLat).setHTML(`<article class="wsi-popup"><header><span>◈</span><div><b>${esc(p.label)}</b><small>DIAGNOSTICA MODELLO · ${esc(eventTrustLabel(p))}</small></div></header><p>${esc(p.explanation)}</p><div class="wsi-popup__provenance">ECMWF IFS · valido ${new Date(p.valid_utc).toLocaleString('it-IT')} · struttura derivata automaticamente</div></article>`).addTo(map);};
    for(const id of [IDS.advancedFill,IDS.advancedLine]){map.on('click',id,popup);map.on('mouseenter',id,()=>map.getCanvas().style.cursor='pointer');map.on('mouseleave',id,()=>map.getCanvas().style.cursor='');}
}

function setAdvancedVisible(on){setImageVisible(IDS.advancedFill,on);setImageVisible(IDS.advancedLine,on);}

function mergedTerritorialDocument() {
    const base=territorialDocument&&typeof territorialDocument==='object'?territorialDocument:{};
    const baseEvents=Array.isArray(base.events)?base.events:[];
    const top=state?.relational_intelligence?.top_diagnoses;
    const all=state?.relational_intelligence?.diagnoses;
    const diagnoses=Array.isArray(top)&&top.length?top:Array.isArray(all)?all:[];
    if(!diagnoses.length)return base;
    const selected=new Map();
    for(const [origin,events] of [['territorial',baseEvents],['relational',diagnoses]]){
        for(const [index,event] of events.entries()){
            if(!event||typeof event!=='object')continue;
            const key=event.diagnosis_key?`diagnosis:${event.diagnosis_key}`:`id:${event.id||`${origin}-${index}`}`;
            const current=selected.get(key);
            if(!current){selected.set(key,{event,origin});continue;}
            if(newerDiagnosisSnapshot(event,current.event,origin,current.origin)){
                selected.set(key,{event,origin});
            }
        }
    }
    return {...base,events:[...selected.values()].map(item=>item.event)};
}

function territorialPopupHtml(properties) {
    const p=properties||{};
    const phase={observed:'OSSERVATO',nowcast:'NOWCAST',forecast:'PREVISIONE',potential:'POTENZIALE'}[p.phase]||p.phase||'DIAGNOSI';
    const trust=eventTrustLabel(p);
    const isCorridor=p.geometry_kind==='diagnostic_corridor';
    const semantics=isCorridor?'Corridoio diagnostico, non fronte osservato. ':'';
    const from=p.detected_at_utc||p.valid_from_utc||p.transition_from_utc;
    const to=p.observation_valid_to_utc||p.valid_to_utc||p.transition_to_utc;
    const format=value=>value&&!Number.isNaN(Date.parse(value))?new Date(value).toLocaleString('it-IT'):'n/d';
    return `<article class="wsi-popup"><header><span>${esc(p.icon||'◉')}</span><div><b>${esc(p.title||'Diagnosi Weather Sicily')}</b><small>${esc(phase)} · ${esc(trust)}</small></div></header><p>${esc(p.summary||'Scenario relazionale in elaborazione.')}</p><div class="wsi-popup__provenance">${esc(semantics+(p.explanation||''))} Fonti: ${esc(p.source_labels||'n/d')}.</div>${p.evidence_text?`<b>Evidenze</b><p>${esc(p.evidence_text)}</p>`:''}${p.contradictions_text?`<b>Limiti</b><p>${esc(p.contradictions_text)}</p>`:''}<footer>Valido ${esc(format(from))} → ${esc(format(to))}</footer></article>`;
}

function ensureTerritorialLayers() {
    if(map.getSource(IDS.territorialSrc))return;
    const before=map.getLayer('ws-stations-symbols')?'ws-stations-symbols':undefined;
    map.addSource(IDS.territorialSrc,{type:'geojson',data:{type:'FeatureCollection',features:[]}});
    map.addLayer({id:IDS.territorialTrack,type:'line',source:IDS.territorialSrc,filter:['any',['==',['get','geometry_kind'],'evolution'],['==',['get','geometry_kind'],'diagnostic_corridor']],paint:{
        'line-color':['case',['==',['get','geometry_kind'],'diagnostic_corridor'],'#54d7ff','#ffcf4d'],'line-width':['case',['==',['get','geometry_kind'],'diagnostic_corridor'],3,2.2],'line-opacity':.82,'line-dasharray':[3,2],
    }},before);
    map.addLayer({id:IDS.territorialPoint,type:'circle',source:IDS.territorialSrc,filter:['==',['geometry-type'],'Point'],paint:{
        'circle-color':['case',['has','diagnosis_key'],'#54d7ff',['match',['get','kind'],'ventilation_boundary','#54d7ff','airmass_transition','#54d7ff','thermal_inversion','#6fc7ff','fog','#c9d7e3','downslope_wind','#ff9c45','sea_breeze','#42d6d0','land_breeze','#3189c9','heavy_rain','#3478ff','strong_wind','#ffd34f','convective_cell','#d85cff','surface_convergence','#55d88b','#aab6c5']],
        'circle-radius':['interpolate',['linear'],['zoom'],5,5,10,10,14,15],
        'circle-stroke-width':['case',['any',['==',['get','phase'],'observed'],['has','diagnosis_key']],2.4,1.3],
        'circle-stroke-color':['case',['any',['==',['get','phase'],'observed'],['has','diagnosis_key']],'#ffffff','#101827'],
        'circle-opacity':['case',['==',['get','phase'],'potential'],.48,.82],
    }},before);
    const popup=e=>{const p=e.features?.[0]?.properties;if(!p)return;territorialPopup?.remove();territorialPopup=new maplibregl.Popup({className:'wsi-popup-shell',maxWidth:'390px',offset:14}).setLngLat(e.lngLat).setHTML(territorialPopupHtml(p)).addTo(map);};
    for(const id of [IDS.territorialPoint,IDS.territorialTrack]){map.on('click',id,popup);map.on('mouseenter',id,()=>map.getCanvas().style.cursor='pointer');map.on('mouseleave',id,()=>map.getCanvas().style.cursor='');}
}
function setTerritorialVisible(on){setImageVisible(IDS.territorialPoint,on);setImageVisible(IDS.territorialTrack,on);}
function updateTerritorialScenario(targetMs) {
    if(!territorialGeoJSON)return 0;
    const data=territorialScenarioForTime(territorialGeoJSON,mergedTerritorialDocument(),targetMs);
    ensureTerritorialLayers();map.getSource(IDS.territorialSrc)?.setData(data);
    setTerritorialVisible(visible.territorial);
    return (data.features||[]).filter(feature=>feature.geometry?.type==='Point').length;
}
async function loadTerritorial(){
    const geoFile=state?.territorial_intelligence?.geojson;
    const jsonFile=state?.territorial_intelligence?.json;
    if(!geoFile)return;
    const [geoResponse,jsonResponse]=await Promise.all([
        fetch(cycleAssetUrl(geoFile),{cache:'force-cache',credentials:'same-origin'}),
        jsonFile?fetch(cycleAssetUrl(jsonFile),{cache:'force-cache',credentials:'same-origin'}):Promise.resolve(null),
    ]);
    if(!geoResponse.ok)throw new Error(`territoriale ${geoResponse.status}`);
    territorialGeoJSON=await geoResponse.json();
    territorialDocument=jsonResponse?.ok?await jsonResponse.json():state?.territorial_intelligence;
    ensureTerritorialLayers();updateTerritorialScenario(selectedScenarioTimeMs??Date.now());
}

const PHYSICS_FIELDS={ivt_kg_m_s:{label:'Trasporto vapore IVT',unit:'kg m⁻¹ s⁻¹',min:0,max:400},k_index_c:{label:'Instabilità K index',unit:'°C',min:5,max:40},shear_850_300_kmh:{label:'Shear 850–300 hPa',unit:'km/h',min:0,max:120},rh2_pct:{label:'Umidità relativa 2 m',unit:'%',min:0,max:100},wetbulb_zero_m:{label:'Zero termico di bulbo umido',unit:'m',min:0,max:5000},omega700_pa_s:{label:'Sollevamento a 700 hPa',unit:'Pa/s',min:-1,max:1,invert:true},precip_3h_mm:{label:'Precipitazione 3 ore',unit:'mm',min:0,max:30}};
function physicsColor(value,cfg){let t=(value-cfg.min)/(cfg.max-cfg.min);if(cfg.invert)t=1-t;t=Math.max(0,Math.min(1,t));const stops=[[17,30,66],[31,105,177],[49,205,164],[255,215,72],[244,91,79],[170,48,126]];const p=t*(stops.length-1),a=stops[Math.floor(p)],b=stops[Math.min(stops.length-1,Math.ceil(p))],f=p-Math.floor(p);return a.map((v,i)=>Math.round(v+(b[i]-v)*f));}
function ensurePhysicsLayer(grid){if(physicsCanvas)return;physicsCanvas=document.createElement('canvas');physicsCanvas.width=grid.nx;physicsCanvas.height=grid.ny;const coordinates=[[grid.lon_left,grid.lat_top],[grid.lon_right,grid.lat_top],[grid.lon_right,grid.lat_bottom],[grid.lon_left,grid.lat_bottom]];const before=map.getLayer('ws-stations-symbols')?'ws-stations-symbols':undefined;map.addSource(IDS.physicsSrc,{type:'canvas',canvas:physicsCanvas,coordinates,animate:false});map.addLayer({id:IDS.physics,type:'raster',source:IDS.physicsSrc,paint:{'raster-opacity':.64,'raster-fade-duration':0,'raster-resampling':'linear'}},before);map.setLayoutProperty(IDS.physics,'visibility','none');}
function renderPhysics(grid,key){const cfg=PHYSICS_FIELDS[key],values=grid.fields?.[key];if(!cfg||!values)return;ensurePhysicsLayer(grid);const ctx=physicsCanvas.getContext('2d'),img=ctx.createImageData(grid.nx,grid.ny);for(let i=0;i<values.length;i++){const o=i*4,v=Number(values[i]);if(v===grid.missing_value||!Number.isFinite(v)){img.data[o+3]=0;continue;}const c=physicsColor(v,cfg);img.data[o]=c[0];img.data[o+1]=c[1];img.data[o+2]=c[2];img.data[o+3]=205;}ctx.putImageData(img,0,0);refreshCanvasSource(map,IDS.physicsSrc);setImageVisible(IDS.physics,true);currentPhysics=grid;const legend=panel?.querySelector('#wsi-physics-legend');if(legend){legend.hidden=false;legend.innerHTML=`<b>${esc(cfg.label)}</b><i></i><span>${cfg.min} → ${cfg.max} ${cfg.unit}</span><small>ECMWF IFS · valido ${new Date(grid.valid_utc).toLocaleString('it-IT')}</small>`;}}
async function loadPhysics(index){const select=panel?.querySelector('#wsi-physics-select'),key=select?.value;if(!key||key==='off'){setImageVisible(IDS.physics,false);const l=panel?.querySelector('.wsi-physics-legend');if(l)l.hidden=true;return;}const valid=state?.overlays?.timesteps?.[index]?.valid_utc,target=(state?.atmospheric_physics?.timesteps||[]).find(x=>Math.abs(new Date(x.valid_utc)-new Date(valid))<60000);if(!target){setImageVisible(IDS.physics,false);return;}const req=++physicsRequest,response=await fetch(cycleAssetUrl(target.file),{cache:'force-cache',credentials:'same-origin'});if(!response.ok)throw new Error(`physics ${response.status}`);const grid=await response.json();if(req!==physicsRequest)return;renderPhysics(grid,key);}

async function loadAdvanced(index) {
    const step=state?.overlays?.timesteps?.[index],file=step?.advanced_file;if(!file)return;
    const req=++advancedRequest,response=await fetch(cycleAssetUrl(file),{cache:'force-cache',credentials:'same-origin'});if(!response.ok)throw new Error(`diagnostica ${response.status}`);
    const data=await response.json();if(req!==advancedRequest)return;
    const receipt=window.WSIntelligenceScenarioContractReceipt;if(receipt)receipt.advanced_valid_utc=data.valid_utc||step.valid_utc||null;
    ensureAdvancedLayers();map.getSource(IDS.advancedSrc).setData(data);
    setAdvancedVisible(panel?.querySelector('#wsi-advanced')?.checked!==false);
    const counts={};for(const f of data.features||[]){const k=f.properties?.kind||'altro';counts[k]=(counts[k]||0)+1;}
    const names={front:'fronti',pressure_gradient:'gradiente barico',jet_streak:'jet',warm_advection:'avvezione calda',cold_advection:'avvezione fredda',surface_convergence:'convergenza',pressure_fall:'pressione in calo',pressure_rise:'pressione in aumento'};
    const box=panel?.querySelector('#wsi-advanced-summary');if(box){const d=data.diagnostics||{};box.innerHTML=`<b>◉ JARVIS SINOTTICO</b><p>${esc(data.briefing_it||'Briefing non disponibile per questa scadenza.')}</p><span>${(data.features||[]).length} strutture · jet max ${d.max_jet_kmh??'n/d'} km/h · gradiente ${d.max_pressure_gradient_hpa_per_100km??'n/d'} hPa/100 km</span><small>${Object.entries(counts).map(([k,v])=>`${names[k]||k} ${v}`).join(' · ')}</small>`;}
}

async function fetchGrid(step) {
    if(!step) return null;
    const key=`${publicationCycle}:${state.model?.run_utc||''}:${step.file}`;
    if(gridCache.has(key)) return gridCache.get(key);
    const promise=fetch(cycleAssetUrl(step.file),{cache:'force-cache',credentials:'same-origin'}).then(r=>{if(!r.ok)throw new Error(`griglia ${r.status}`);return r.json();});
    gridCache.set(key,promise); return promise;
}

async function loadBaricGrid(index) {
    const step=state?.overlays?.timesteps?.[index]; if(!step)return;
    const request=++gridRequest, grid=await fetchGrid(step); if(request!==gridRequest)return;
    currentGrid=grid;
    if(!verifyDisplayedField(grid))return;
    const receipt=window.WSIntelligenceScenarioContractReceipt;if(receipt)receipt.grid_valid_utc=grid.valid_utc||step.valid_utc||null;
    renderPressureGrid(grid);setImageVisible(IDS.pressure,shadeMode!=='off');setIsobarsVisible(visible.isobars);
    if(panel?.querySelector('#wsi-wind')?.checked)setWindAnimated(true);
    updateSynopticTrust(index,grid);if(userLocation)updateLocalAnalysis();
}

function setDisplayTrust(status,textValue) {
    const badge=panel?.querySelector('#wsi-display-trust');if(!badge)return;
    badge.className='wsi-display-trust '+status;badge.textContent=textValue;
}

function verifyDisplayedField(grid) {
    if(grid.wind_units!=='km_h'||!Array.isArray(grid.renderer_probes)||grid.renderer_probes.length<6){
        setDisplayTrust('bad','Contratto incompleto');setWindAnimated(false);setImageVisible(IDS.pressure,false);setIsobarsVisible(false);return false;
    }
    const tolerance=.05;
    const animation=window.WSIntelligenceDisplayContract?.animationSelfTest?.();
    if(animation?.pass!==true){setDisplayTrust('bad','Errore scala animazione');setWindAnimated(false);return false;}
    const ok=grid.renderer_probes.every(probe=>{
        const u=fieldAt(grid,'u10',Number(probe.lat),Number(probe.lon));
        const v=fieldAt(grid,'v10',Number(probe.lat),Number(probe.lon));
        const pressure=fieldAt(grid,'mslp',Number(probe.lat),Number(probe.lon));
        return Number.isFinite(u)&&Number.isFinite(v)&&Number.isFinite(pressure)&&
            Math.abs(u-Number(probe.u_kmh))<=tolerance&&Math.abs(v-Number(probe.v_kmh))<=tolerance&&
            Math.abs(pressure-Number(probe.mslp_hpa))<=.011;
    });
    if(!ok){setDisplayTrust('bad','Errore renderer');setWindAnimated(false);setImageVisible(IDS.pressure,false);setIsobarsVisible(false);return false;}
    setDisplayTrust('ok','Display verificato');
    return true;
}

function updateSynopticTrust(index,grid) {
    const el=panel?.querySelector('#wsi-synoptic-trust');if(!el||!grid)return;
    const step=state.overlays.timesteps[index],valid=new Date(grid.valid_utc),now=new Date();
    const mode=step?.kind==='analisi'?'ANALISI ARCHIVIATA':(valid-now)>36e5?'PREVISIONE':'ANALISI CORRENTE';
    const lead=Number(grid.step_h||0),sat=state?.satellite,frame=sat?.frame_time_utc?new Date(sat.frame_time_utc):null;
    const verifiedValid=sat?.model_valid_utc?new Date(sat.model_valid_utc):null;
    const comparable=frame&&verifiedValid&&Math.abs(valid-verifiedValid)<=30*60000;
    const integrity=state?.integrity||{},integrityLabel=integrity.status==='verified'&&!integrity.model_run_stale?'INTEGRITÀ VERIFICATA':'INTEGRITÀ DEGRADATA';
    const consensus=state?.model_sources?.consensus,modelNames=consensus?.models_available||[];
    const modelProof=consensus?`<em>Consenso barico indipendente all’ora corrente: ${esc(modelNames.join(' + '))} · fonti ufficiali dirette, griglie in cache.</em>`:'';
    el.innerHTML=`<div><b>${mode}</b><strong>ECMWF IFS 0.25°</strong></div><span>Run ${esc(grid.run_utc||state.model?.run_utc||'n/d')} · valido ${valid.toLocaleString('it-IT',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})}${step?.kind==='run'?` · +${lead}h`:''}</span><small>MSLP e vento a 10 m da modello numerico; non sono misure dirette. U/V in km/h, griglia 0,25° (~22–28 km), interpolazione bilineare: valli e rilievi più piccoli non sono risolti.</small><mark class="${integrity.status==='verified'&&!integrity.model_run_stale?'ok':'bad'}">${integrityLabel} · campi ${integrity.field_alignment_ok?'allineati':'non allineati'} · ${integrity.advanced_timestep_count||0}/${integrity.expected_advanced_timesteps||0} diagnostiche</mark>${modelProof}${comparable?`<em>Verifica MTG del pattern nuvoloso: accordo ${esc(sat.agreement_pct)}% al frame ${frame.toLocaleTimeString('it-IT',{hour:'2-digit',minute:'2-digit'})}</em>`:'<em>Il confronto satellitare vale vicino all’ora corrente, non sulle scadenze future.</em>'}`;
}

function pressureAt(grid,lat,lon) {
    if(!grid||lat>grid.lat_top||lat<grid.lat_bottom||lon<grid.lon_left||lon>grid.lon_right)return null;
    const x=(lon-grid.lon_left)/(grid.lon_right-grid.lon_left)*(grid.nx-1),y=(grid.lat_top-lat)/(grid.lat_top-grid.lat_bottom)*(grid.ny-1);
    const x0=Math.floor(x),y0=Math.floor(y),x1=Math.min(grid.nx-1,x0+1),y1=Math.min(grid.ny-1,y0+1),fx=x-x0,fy=y-y0;
    const a=grid.mslp[y0*grid.nx+x0]*(1-fx)+grid.mslp[y0*grid.nx+x1]*fx;
    const b=grid.mslp[y1*grid.nx+x0]*(1-fx)+grid.mslp[y1*grid.nx+x1]*fx;
    return a*(1-fy)+b*fy;
}

function haversine(a,b,c,d){const R=6371,q=x=>x*Math.PI/180,dl=q(c-a),dn=q(d-b),s=Math.sin(dl/2)**2+Math.cos(q(a))*Math.cos(q(c))*Math.sin(dn/2)**2;return 2*R*Math.asin(Math.sqrt(s));}

async function updateLocalAnalysis() {
    const box=panel?.querySelector('#wsi-local');if(!box||!userLocation||!currentGrid)return;
    const {lat,lon}=userLocation,p=pressureAt(currentGrid,lat,lon);if(p==null){box.hidden=false;box.innerHTML='<b>Posizione fuori dal dominio barico europeo.</b>';return;}
    const u=fieldAt(currentGrid,'u10',lat,lon),v=fieldAt(currentGrid,'v10',lat,lon);
    const dirs=['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSO','SO','OSO','O','ONO','NO','NNO'];
    const bearing=u==null||v==null?null:(Math.atan2(-u,-v)*180/Math.PI+360)%360;
    const wind=u==null||v==null?'vento n/d':`vento ${Math.hypot(u,v)<1?'calmo':`${Math.hypot(u,v).toFixed(0)} km/h da ${dirs[Math.round(bearing/22.5)%16]}`}`;
    const T=new Date(currentGrid.valid_utc).getTime();
    const scenarioFrame=scenarioFrameForTime(state,T);
    const nearest=(scenarioFrame?.centers||[]).filter(f=>f.kind==='low'||f.kind==='high').map(f=>({f,q:f,d:haversine(lat,lon,f.lat,f.lon)})).sort((a,b)=>a.d-b.d)[0];
    let trend='';const prev=state.overlays.timesteps[stepIndex-1];
    if(prev){try{const pg=await fetchGrid(prev),pp=pressureAt(pg,lat,lon),hours=(new Date(currentGrid.valid_utc)-new Date(pg.valid_utc))/36e5;if(pp!=null&&hours>0){const delta=p-pp;trend=` · ${delta>=0?'+':''}${delta.toFixed(1)} hPa/${Math.round(hours)}h`;}}catch(e){}}
    const sys=nearest?`${nearest.f.kind==='low'?'Bassa':'Alta'} più vicina: ${esc(nearest.f.label)}, ${Math.round(nearest.d)} km`:'Nessun centro classificato vicino';
    box.hidden=false;box.innerHTML=`<b>📍 Analisi barica locale</b><strong>${p.toFixed(1)} hPa${trend}</strong><span>${sys} · ${wind}</span><small>ECMWF IFS · valido ${new Date(currentGrid.valid_utc).toLocaleString('it-IT',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})}</small>`;
}

function locateBaric() {
    const box=panel.querySelector('#wsi-local');box.hidden=false;box.innerHTML='<b>📍 Localizzazione in corso…</b>';
    if(!navigator.geolocation){box.innerHTML='<b>Geolocalizzazione non disponibile.</b>';return;}
    navigator.geolocation.getCurrentPosition(pos=>{
        userLocation={lat:pos.coords.latitude,lon:pos.coords.longitude};
        if(userMarker)userMarker.remove();const el=document.createElement('div');el.className='wsi-user-location';el.title='La tua posizione';userMarker=new maplibregl.Marker({element:el}).setLngLat([userLocation.lon,userLocation.lat]).addTo(map);
        map.flyTo({center:[userLocation.lon,userLocation.lat],zoom:7.2,duration:900});updateLocalAnalysis();
    },()=>{box.innerHTML='<b>Posizione non autorizzata.</b><span>Abilita la localizzazione del browser per l’analisi barica intorno a te.</span>';},{enableHighAccuracy:true,timeout:10000,maximumAge:300000});
}

function trackPointTime(point) {
    return Date.parse(point?.valid_utc??point?.t??'');
}

function scenarioTrackFor(featureId) {
    return (state?.synoptic_scenario?.tracks||[]).find(track=>String(track.id)===String(featureId))||null;
}

function pointsForFeature(feature) {
    const scenarioTrack=scenarioTrackFor(feature.id);
    return scenarioTrack?.points||[];
}

function splitTrackSegments(points) {
    const result=[];let segment=[];
    for(const point of points){
        const previous=segment[segment.length-1];
        const previousStep=Number(previous?.step_index),step=Number(point?.step_index);
        const contiguous=!previous
            ||(Number.isInteger(previousStep)&&Number.isInteger(step)?step-previousStep===1:trackPointTime(point)-trackPointTime(previous)<=3.5*36e5);
        if(!contiguous){if(segment.length>1)result.push(segment);segment=[];}
        segment.push(point);
    }
    if(segment.length>1)result.push(segment);
    return result;
}

function tracksGeoJSON(direction,frame) {
    if(!frame)return {type:'FeatureCollection',features:[]};
    const active=new Map((frame.centers||[]).map(feature=>[String(feature.id),feature]));
    const features=[];
    active.forEach(feature=>{
        const selected=(pointsForFeature(feature)||[])
            .filter(point=>Number.isFinite(trackPointTime(point)))
            .sort((a,b)=>trackPointTime(a)-trackPointTime(b))
            .filter(point=>direction==='past'?trackPointTime(point)<=frame.valid_ms:trackPointTime(point)>=frame.valid_ms);
        splitTrackSegments(selected).forEach(segment=>features.push({
            type:'Feature',
            properties:{kind:feature.kind,label:feature.label,track_id:feature.id,direction},
            geometry:{type:'LineString',coordinates:segment.map(point=>[Number(point.lon),Number(point.lat)])},
        }));
    });
    return {type:'FeatureCollection',features};
}

function drawTracks(frame=null) {
    for (const [id,direction,dash,opacity] of [[IDS.past,'past',[1,0],.4],[IDS.future,'future',[3,2],.85]]) {
        const src=map.getSource(id);
        const data=tracksGeoJSON(direction,frame);
        if (src) src.setData(data);
        else {
            map.addSource(id,{type:'geojson',data});
            map.addLayer({id,type:'line',source:id,paint:{'line-color':['match',['get','kind'],'low','#ff4257','high','#3b8cff','#ad68ff'],
                'line-width':2,'line-opacity':opacity,'line-dasharray':dash}});
            map.setLayoutProperty(id,'visibility',visible.tracks?'visible':'none');
        }
    }
}

function popupHtml(x) {
    const sources=(x.sources || []).map(s => s.label).join(' + ');
    const evidence=(x.evidence || []).slice(0,5).map(e => `<li>${esc(e)}</li>`).join('');
    return `<article class="wsi-popup wsi-popup--${esc(x.severity)}">
      <header><span>${esc(x.icon || '◆')}</span><div><b>${esc(x.title)}</b><small>${esc(x.category)} · ${esc(eventTrustLabel(x))}</small></div></header>
      <p>${esc(x.summary)}</p><div class="wsi-popup__source">${esc(sources)}</div>
      ${evidence ? `<ul>${evidence}</ul>` : ''}
      <footer>${x.duration_min ? `attivo da ${esc(x.duration_min)} min` : 'appena rilevato'} · ${x.occurrences || 1} conferme</footer>
    </article>`;
}

function compactTrustLabel(value) {
    const label=eventTrustLabel(value);
    if(label.startsWith('Probabilità '))return label.slice('Probabilità '.length);
    if(label.startsWith('Evidenza '))return 'evidenza';
    return '—';
}

function addInsightMarkers() {
    markers.forEach(m => m.remove()); markers=[];
    const insights=state?.live?.total_fusion?.insights || [];
    insights.filter(x => x.lat != null && x.lon != null).forEach(x => {
        const el=document.createElement('button');
        el.className='wsi-map-marker'; el.style.setProperty('--wsi-color',color(x.severity));
        el.textContent=x.icon || '◆'; el.title=x.title;
        const marker=new maplibregl.Marker({element:el,anchor:'center'}).setLngLat([x.lon,x.lat]).addTo(map);
        el.hidden=!visible.insights;
        el.addEventListener('click',e => {
            e.stopPropagation();
            new maplibregl.Popup({className:'wsi-popup-shell',maxWidth:'380px',offset:18,closeButton:true})
                .setLngLat([x.lon,x.lat]).setHTML(popupHtml(x)).addTo(map);
        });
        markers.push(marker);
    });
}

function featureClass(f) { return f.kind === 'high' ? 'high' : (f.kind === 'low' ? 'low' : 'upper'); }
function featureLetter(f) { return f.kind === 'high' ? 'A' : (f.kind === 'low' ? 'B' : 'C'); }
function featureMeasure(f, point=f) {
    if (f.kind === 'upper_low') return `${point.gh500_dam ?? f.gh500_dam ?? 'n/d'} dam a 500 hPa`;
    return `${point.mslp_hpa ?? f.mslp_hpa ?? 'n/d'} hPa MSLP`;
}
function featureThermal(f) {
    return f.kind === 'upper_low' ? `T500 ${f.t500_c ?? 'n/d'}°C` : `T850 ${f.t850_c ?? 'n/d'}°C`;
}
function featureProvenance(f) {
    return ['sat_corr','satellite_correlated','satellite'].includes(f.position_source)
        ? 'Posizione del pattern nuvoloso correlata con MTG; il valore atmosferico resta derivato da ECMWF.'
        : 'Posizione e valore atmosferico derivati dal modello ECMWF.';
}
function featureConsensusHtml(f) {
    const rows=Object.entries(f.models||{}).map(([name,r])=>`<li><b>${esc(name)}</b>: ${esc(r.verdict||'dato disponibile')} · ${esc(r.min_hpa)} hPa · run ${new Date(r.run_utc).toLocaleString('it-IT',{day:'2-digit',hour:'2-digit',minute:'2-digit'})}</li>`).join('');
    if(!rows)return '';
    const c=f.models_consensus||{};
    return `<b>Consenso barico indipendente</b><small>${esc(c.confirmed??0)}/${esc(c.available??0)} modelli confermano un minimo chiuso · stessa ora valida</small><ul>${rows}</ul>`;
}

function featurePopupHtml(record) {
    const feature=record.feature;
    const future=pointsForFeature(feature).filter(point=>trackPointTime(point)>record.validMs).slice(0,5)
        .map(point=>`<li>${new Date(trackPointTime(point)).toLocaleString('it-IT',{day:'2-digit',hour:'2-digit',minute:'2-digit'})}: ${esc(featureMeasure(feature,point))}</li>`).join('');
    const speed=Number(feature.motion?.speed_kmh);
    const motion=Number.isFinite(speed)?`Moto ${esc(feature.motion?.toward||'calcolato')} ${Math.round(speed)} km/h`:'Moto non stimato su questa singola scadenza';
    const lifecycle=feature.lifecycle||{};
    const lifecycleName=typeof lifecycle==='string'?lifecycle:null;
    const lifecycleLabels={genesis:'genesi prevista',reacquired:'riagganciata dopo un gap',lysis:'termine della struttura',ephemeral:'struttura di breve durata',active:'struttura attiva'};
    const lifecycleText=lifecycleName?(lifecycleLabels[lifecycleName]||lifecycleName):lifecycle.genesis&&lifecycle.lysis?'genesi e lisi nello stesso step':lifecycle.genesis?'genesi prevista':lifecycle.lysis?'termine della struttura':'struttura attiva';
    const source=['sat_corr','satellite_correlated','satellite'].includes(feature.position_source)?'FUSIONE LIVE + MODELLO':'SCENARIO MODELLO';
    return `<article class="wsi-popup"><header><span>${featureLetter(feature)}</span><div><b>${esc(feature.label||feature.id)}</b><small>${source} · ${esc(lifecycleText)}</small></div></header><p><b>${esc(featureMeasure(feature))}</b> · ${esc(featureThermal(feature))}. ${motion}.</p><div class="wsi-popup__provenance">${esc(featureProvenance(feature))} Valido ${new Date(record.validMs).toLocaleString('it-IT')}.</div>${featureConsensusHtml(feature)}${future?`<b>Traiettoria modellistica successiva</b><ul>${future}</ul>`:''}</article>`;
}

function reconcileFeatureMarkers(frame) {
    const seen=new Set();
    (frame?.centers||[]).forEach((raw,index)=>{
        const lat=Number(raw.lat),lon=Number(raw.lon);if(!Number.isFinite(lat)||!Number.isFinite(lon))return;
        const id=String(raw.id??`${raw.kind||'center'}-${index}`);seen.add(id);
        let record=featureMarkers.get(id);
        if(!record){
            const el=document.createElement('button');
            const marker=new maplibregl.Marker({element:el,anchor:'center'}).setLngLat([lon,lat]).addTo(map);
            record={marker,el,feature:null,validMs:frame.valid_ms};
            el.addEventListener('click',event=>{
                event.stopPropagation();
                new maplibregl.Popup({className:'wsi-popup-shell',maxWidth:'390px',offset:22})
                    .setLngLat(record.marker.getLngLat()).setHTML(featurePopupHtml(record)).addTo(map);
            });
            featureMarkers.set(id,record);
        }
        record.feature={...raw,id};record.validMs=frame.valid_ms;
        record.el.className='wsi-pressure '+featureClass(record.feature);
        record.el.textContent=featureLetter(record.feature);
        record.el.title=record.feature.label||id;
        record.el.dataset.featureId=id;
        record.el.hidden=!visible.centers;
        record.marker.setLngLat([lon,lat]);
    });
    for(const [id,record] of featureMarkers){
        if(!seen.has(id)){record.marker.remove();featureMarkers.delete(id);}
    }
}

function currentScenarioFrame(frame) {
    const horizon=getScenarioHorizon(state);
    return horizon?.now_index!==null&&horizon?.now_index!==undefined&&frame?.index===horizon.now_index;
}

function syncLiveOnlyLayers(isCurrent) {
    if(!document.documentElement.classList.contains('wsi-product'))return;
    if(!isCurrent){
        if(!liveOnlyLayerSnapshot){
            liveOnlyLayerSnapshot={
                toggles:new Map(LIVE_ONLY_TOGGLE_IDS.map(id=>[id,document.getElementById(id)?.checked===true])),
                panelSatellite:panel?.querySelector('#wsi-satellite')?.checked===true,
            };
        }
        for(const id of LIVE_ONLY_TOGGLE_IDS){
            const toggle=document.getElementById(id);
            if(toggle?.checked){toggle.checked=false;toggle.dispatchEvent(new Event('change',{bubbles:true}));}
        }
        const satellite=panel?.querySelector('#wsi-satellite');if(satellite)satellite.checked=false;
        return;
    }
    if(!liveOnlyLayerSnapshot)return;
    const snapshot=liveOnlyLayerSnapshot;liveOnlyLayerSnapshot=null;
    snapshot.toggles.forEach((checked,id)=>{
        const toggle=document.getElementById(id);
        if(toggle&&toggle.checked!==checked){toggle.checked=checked;toggle.dispatchEvent(new Event('change',{bubbles:true}));}
    });
    if(snapshot.panelSatellite)setPanelToggle('wsi-satellite',true);
}

function setLiveInsightVisibility(isCurrent) {
    markers.forEach(marker=>{marker.getElement().hidden=!visible.insights||!isCurrent;});
    const attention=panel?.querySelector('.wsi-attention');if(attention)attention.hidden=!isCurrent;
}

function updateScenarioNarrative(frame,isCurrent,territorialCount) {
    const section=panel?.querySelector('.wsi-now');if(!section)return;
    const status=panel?.querySelector('.wsi-statusline > span:first-child');
    if(status){status.classList.toggle('ok',isCurrent);status.textContent=isCurrent?'● Operativo live':'◆ Scenario temporale';}
    const valid=new Date(frame.valid_ms);
    section.querySelector('b').textContent=isCurrent?'Cosa sta accadendo':frame.valid_ms>(getScenarioHorizon(state)?.now_ms??Date.now())?'Scenario previsto':'Scenario ricostruito';
    const text=section.querySelector('p');
    if(isCurrent)text.textContent=state.headline_it||'Scenario corrente ricostruito dai dati disponibili.';
    else text.textContent=`${frame.centers.length} centri sinottici e ${territorialCount} fenomeni territoriali attivi alla scadenza ${valid.toLocaleString('it-IT',{weekday:'long',day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})}. Campi ECMWF, diagnostica fisica e previsioni locali sono allineati allo stesso istante.`;
    const territorialSummary=panel?.querySelector('.wsi-territorial-summary span');
    if(territorialSummary)territorialSummary.textContent=`${territorialCount} eventi attivi alla scadenza selezionata`;
}

function updateScenarioReceipt(frame,available=true,territorialCount=0) {
    const receipt=window.WSIntelligenceScenarioContractReceipt;
    if(!receipt)return;
    receipt.available=available;
    receipt.source=frame?.source||null;
    receipt.selected_utc=frame?new Date(frame.valid_ms).toISOString():(Number.isFinite(selectedScenarioTimeMs)?new Date(selectedScenarioTimeMs).toISOString():null);
    if(available){if(Date.parse(receipt.grid_valid_utc)!==frame.valid_ms)receipt.grid_valid_utc=null;if(Date.parse(receipt.advanced_valid_utc)!==frame.valid_ms)receipt.advanced_valid_utc=null;}
    receipt.step_index=frame?.index??null;
    receipt.active_center_ids=(frame?.centers||[]).map(feature=>String(feature.id));
    receipt.active_center_count=receipt.active_center_ids.length;
    receipt.active_territorial_count=available?territorialCount:0;
    if(!available){receipt.grid_valid_utc=null;receipt.advanced_valid_utc=null;}
}

function clearScenarioDisplay(targetMs) {
    selectedScenarioTimeMs=targetMs;
    scenarioApplySequence+=1;gridRequest+=1;advancedRequest+=1;physicsRequest+=1;
    reconcileFeatureMarkers({centers:[],valid_ms:targetMs});drawTracks(null);
    map.getSource(IDS.advancedSrc)?.setData({type:'FeatureCollection',features:[]});
    map.getSource(IDS.territorialSrc)?.setData({type:'FeatureCollection',features:[]});
    setImageVisible(IDS.pressure,false);setIsobarsVisible(false);setAdvancedVisible(false);setImageVisible(IDS.physics,false);
    if(windActive)setWindAnimated(false);
    setLiveInsightVisibility(false);syncLiveOnlyLayers(false);
    const label=panel?.querySelector('#wsi-time-label');
    if(label)label.textContent=`Scenario non disponibile · ${new Date(targetMs).toLocaleString('it-IT')}`;
    const section=panel?.querySelector('.wsi-now');
    if(section){section.querySelector('b').textContent='Copertura terminata';section.querySelector('p').textContent='Nessun dato viene congelato oltre l’intervallo realmente disponibile.';}
    updateScenarioReceipt(null,false);
    updateIntelligenceWorkspaceContext({state, mode:workspaceTemporalMode(targetMs), targetMs, territorialCount:0});
}

function overlayIndexForTime(targetMs) {
    const steps=state?.overlays?.timesteps||[];let best=-1,delta=Infinity;
    steps.forEach((step,index)=>{const d=Math.abs(Date.parse(step.valid_utc)-targetMs);if(d<delta){delta=d;best=index;}});
    return best;
}

function applyScenarioFrame(frame,index) {
    scenarioApplySequence+=1;
    selectedScenarioTimeMs=frame.valid_ms;
    const isCurrent=currentScenarioFrame(frame);
    reconcileFeatureMarkers(frame);drawTracks(frame);
    const territorialCount=updateTerritorialScenario(frame.valid_ms);
    setTracksVisible(visible.tracks);
    setLiveInsightVisibility(isCurrent);syncLiveOnlyLayers(isCurrent);
    const valid=new Date(frame.valid_ms);
    const horizon=getScenarioHorizon(state);
    const role=isCurrent?'Analisi corrente':frame.valid_ms>(horizon?.now_ms??Date.now())?'Previsione':'Analisi modello';
    const label=panel?.querySelector('#wsi-time-label');
    if(label)label.textContent=`${role} · ${valid.toLocaleString('it-IT',{weekday:'short',day:'2-digit',hour:'2-digit',minute:'2-digit'})} · ${frame.centers.length} centri`;
    updateScenarioNarrative(frame,isCurrent,territorialCount);updateScenarioReceipt(frame,true,territorialCount);
    updateIntelligenceWorkspaceContext({state, mode:workspaceTemporalMode(frame.valid_ms), targetMs:frame.valid_ms, territorialCount});
    loadBaricGrid(index).catch(err=>console.warn('[WS Monitor griglia barica]',err));
    loadAdvanced(index).catch(err=>console.warn('[WS Monitor diagnostica]',err));
    loadPhysics(index).catch(err=>console.warn('[WS Monitor physics]',err));
}

export function showIntelligenceAtTime(target) {
    const targetMs=target instanceof Date?target.getTime():typeof target==='number'?target:Date.parse(String(target||''));
    if(!Number.isFinite(targetMs))return false;
    pendingScenarioTimeMs=targetMs;
    if(!state||!panel)return false;
    const frame=scenarioFrameForTime(state,targetMs);
    if(!frame){clearScenarioDisplay(targetMs);return false;}
    const index=overlayIndexForTime(frame.valid_ms);
    if(index<0){clearScenarioDisplay(targetMs);return false;}
    pendingScenarioTimeMs=null;stepIndex=index;
    const slider=panel.querySelector('#wsi-time');if(slider)slider.value=String(index);
    applyScenarioFrame(frame,index);
    return true;
}

function moveFeatures(index) {
    const target=state?.overlays?.timesteps?.[index];if(!target)return false;
    return showIntelligenceAtTime(target.valid_utc);
}

window.WSIntelligenceScenarioContractReceipt={available:false,source:null,selected_utc:null,step_index:null,active_center_ids:[],active_center_count:0,active_territorial_count:0,grid_valid_utc:null,advanced_valid_utc:null};
window.WSIntelligenceScenarioContract=Object.freeze({
    id:'ws-intelligence-scenario-display-v1',
    schema_version:1,
    getReceipt:()=>({...window.WSIntelligenceScenarioContractReceipt,shade_mode:shadeMode,coverage:getScenarioHorizon(state)}),
});

function renderPanel() {
    const tf=state?.live?.total_fusion || {};
    const insights=tf.insights || [];
    const attention=insights.filter(x => ['critical','warning','watch'].includes(x.severity));
    const attentionTopics=[];
    const topicIndex=new Map();
    for(const item of attention){
        const key=`${item.category||''}|${item.title||''}`.toLowerCase();
        const previous=topicIndex.get(key);
        if(previous){previous.area_count=(previous.area_count||1)+1;continue;}
        const topic={...item,area_count:1};topicIndex.set(key,topic);attentionTopics.push(topic);
    }
    const quality=state?.live?.station_quality || {};
    const territorial=state?.territorial_intelligence||{};
    const physics=state?.atmospheric_physics||{},physicsDiag=physics.diagnostics||{},strong=physicsDiag.strongest_future||{};
    const absolute=state?.absolute_engine||{},ready=absolute.readiness||{};
    const weatherState=state?.weather_state_engine||{};
    const windVerification=state?.wind_verification||{},windHistory=windVerification.history||{};
    const windLatest=windHistory.latest||{},windMetrics=windLatest.overall||{};
    const windPromotion=windVerification.promotion_gate||{};
    const windEvidenceKind=windLatest.prospective?'prospettica (previsione congelata prima della misura)':'bootstrap retrospettivo (solo diagnostica)';
    const geophysical=state?.geophysical_intelligence||{},geoEtna=geophysical.etna||{},geoCameras=geophysical.volcanic_imagery||{};
    const geoTremor=geoEtna.tremor||{},geoInfra=geoEtna.infrasound||{},geoSeismic=geophysical.seismicity||{};
    const geoSea=geophysical.sea_level||{},geoDeformation=geoEtna.deformation||{},geoOfficial=geoEtna.official||{};
    const disabledMetrics=(quality.metrics || []).filter(x=>['disabled','manual_disabled'].includes(x.status));
    const disabledWhy=disabledMetrics.map(x=>`<article><b>${esc(x.station)} · ${esc(x.metric_label || x.metric)} OFF</b><p>${esc(x.explanation || x.reason || 'Motivazione non disponibile')}</p></article>`).join('');
    const satTime=state?.provenance?.satellite?.frame_utc || state?.satellite?.frame_time_utc;
    const modelValid=state?.provenance?.isobars?.valid_utc || state?.model?.valid_now_utc;
    const fmtTime=v=>v ? new Date(v).toLocaleString('it-IT',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}) : 'n/d';
    const steps=state?.overlays?.timesteps || [];
    const horizon=getScenarioHorizon(state);
    const initialTarget=pendingScenarioTimeMs??selectedScenarioTimeMs??horizon?.now_ms??Date.now();
    const initialFrame=scenarioFrameForTime(state,initialTarget);
    if(initialFrame)stepIndex=overlayIndexForTime(initialFrame.valid_ms);
    else if(stepIndex<0||stepIndex>=steps.length)stepIndex=Math.max(0,horizon?.now_index??0);
    const operationalHorizonHours=Math.max(0,Math.floor(horizon?.future_hours??0));
    const territorialHorizonHours=Math.max(0,Math.floor(Number(territorial.horizon_hours??operationalHorizonHours)));
    const technicalDetails=[
        {
            summary:'Perché Intelligence lo dice',
            trustedHtml:`<div class="wsi-provenance"><span class="observed"><b>● OSSERVATO</b><small>MTG ${fmtTime(satTime)}</small></span><span class="model"><b>◆ MODELLO</b><small>ECMWF ${fmtTime(modelValid)}</small></span><span class="forecast"><b>↗ FUTURO</b><small>fino a +${operationalHorizonHours} h operative</small></span></div><div class="wsi-wind-verification"><b>Verifica vento modello ↔ stazioni</b><p>${windLatest.valid_utc?`${esc(windEvidenceKind)} · +${windLatest.step_h??'n/d'} h · valida ${fmtTime(windLatest.valid_utc)}`:'Raccolta prospettica in inizializzazione.'}</p><span>${windMetrics.n?`${windMetrics.n} stazioni · MAE ${windMetrics.speed_mae_kmh??'n/d'} km/h · bias ${windMetrics.speed_bias_kmh>0?'+':''}${windMetrics.speed_bias_kmh??'n/d'} km/h · errore direzione mediano ${windMetrics.direction_median_error_deg??'n/d'}°`:'Metriche non ancora disponibili.'}</span><small>Gate causale ${windPromotion.current??0}/${windPromotion.required_prospective_valid_times??20} date · ${windPromotion.pass_gate?'correzioni locali ammesse':'nessuna correzione appresa applicata alla mappa'}</small></div><div class="wsi-geophysics"><b>Terra · Etna e sismicità</b><p>${esc(geophysical.briefing_it||'Stato geofisico in elaborazione.')}</p><span>Etna ${esc(geoEtna.state||'n/d')} · tremore ${geoTremor.ratio_smooth??'n/d'}× · infrasuono ${geoInfra.ratio??'n/d'}× · VONA ${esc(geoOfficial.vona||'n/d')} · termico ${geoEtna.thermal?.vrp_mw??'n/d'} MW</span><small>VT 24h ${geoEtna.seismicity_vt?.n_24h??'n/d'} · terremoti INGV 24h ${geoSeismic.count_24h??'n/d'} · GNSS freschi ${geoDeformation.fresh_station_count??0}/${geoDeformation.gnss_station_count??0} · telecamere vulcaniche ${geoCameras.fresh_camera_count??0}/${geoCameras.camera_count??0} · mareografi ${geoSea.fresh_gauge_count??0}/${geoSea.gauge_count??0}. Nessuna previsione deterministica di eruzioni o terremoti.</small></div><div class="wsi-synoptic-proof"><div id="wsi-synoptic-trust"></div></div><div class="wsi-advanced-summary" id="wsi-advanced-summary"><b>Analisi sinottica</b><span>elaborazione…</span></div><div class="wsi-territorial-summary"><b>Analisi territoriale · +${territorialHorizonHours}h operative</b><p>${esc(territorial.briefing_it||'Elaborazione territoriale non disponibile.')}</p><span>${territorial.event_count||0} eventi analizzati</span></div><div class="wsi-physics-summary"><b>Fisica atmosferica · +${operationalHorizonHours}h operative</b><p>${strong.name?`${esc(strong.name)}: ambiente ${esc(strong.convective_environment_score)}/100; CAPE ${esc(strong.thermodynamics?.sb_cape_j_kg??'n/d')} J/kg.`:'Profili fisici in elaborazione.'}</p><label>Approfondisci <select id="wsi-physics-select"><option value="off">Nessun campo</option>${Object.entries(PHYSICS_FIELDS).map(([k,v])=>`<option value="${k}">${esc(v.label)}</option>`).join('')}</select></label><div class="wsi-physics-legend" id="wsi-physics-legend" hidden></div></div>`,
        },
        {
            summary:'Stato e affidabilità del motore',
            trustedHtml:`<div class="wsi-absolute-summary"><b>INTELLIGENCE ENGINE</b><div><strong>${ready.score??0}%</strong><span>${esc(ready.mode||'in inizializzazione').replaceAll('_',' ')}</span></div><p>${absolute.usable_source_count||0}/${absolute.source_count||0} sorgenti · ${absolute.observation_domains?.length||0} domini · ${absolute.event_count||0} eventi elaborati.</p><small>${ready.missing?.length?`Modalità degradata: manca ${esc(ready.missing.join(', '))}.`:'Gate operativi superati.'}</small></div><div class="wsi-weather-state"><b>WEATHER STATE</b><p><strong>${weatherState.locality_count||0}</strong> località comprese dal motore.</p><span>WS Now + modelli operativi</span></div><a class="wsi-antivirus" href="https://admin.weathersicily.it/stazioni/" target="_blank" rel="noopener"><b>🛡️ Controllo sensori</b><span>${quality.disabled_metrics_total || quality.auto_disabled_metrics || 0} parametri esclusi</span><small>quarantena motivata e reversibile</small></a>${disabledWhy?`<details class="wsi-antivirus-why"><summary>Dettagli esclusioni</summary>${disabledWhy}</details>`:''}`,
        },
    ];
    const content={
        headline:state.headline_it||'',
        updatedLabel:new Date(state.generated_at_utc).toLocaleTimeString('it-IT',{hour:'2-digit',minute:'2-digit'}),
        freshSourceCount:tf.fresh_source_count||0,
        sourceCount:tf.source_count||0,
        wsnow:wsnowV3Status(),
        mode:uiMode,
        timelineMax:Math.max(0,steps.length-1),
        timelineValue:stepIndex,
        attentionCount:attentionTopics.length,
        attention:attentionTopics.map(item=>({
            lat:item.lat??'',lon:item.lon??'',color:color(item.severity),icon:item.icon,title:item.title,
            summary:item.summary,sources:item.sources,trust:compactTrustLabel(item),areaCount:item.area_count,
        })),
        shadeMode,
        visible,
        technicalDetails,
        stationParameter:document.getElementById('selectParametro'),
    };
    workspacePanelApi = mountIntelligenceWorkspace(panel, {
        state,
        content,
        onMode: mode => activateMode(mode),
        onTimeline: intent => requestWorkspaceTimeline(intent),
        onSection: section => rememberWorkspaceSection(section),
        onFocus: item => {
            if(item?.diagnosisKey||item?.origin==='relational'){
                visible.territorial=true;
                updateTerritorialScenario(Date.now());
                setTerritorialVisible(true);
            }
            const corridor=Array.isArray(item?.corridor?.coordinates)
                ?item.corridor.coordinates.filter(coordinate=>Array.isArray(coordinate)&&Number.isFinite(Number(coordinate[0]))&&Number.isFinite(Number(coordinate[1])))
                :[];
            if(corridor.length>=2){
                const lons=corridor.map(coordinate=>Number(coordinate[0])),lats=corridor.map(coordinate=>Number(coordinate[1]));
                map.fitBounds([[Math.min(...lons),Math.min(...lats)],[Math.max(...lons),Math.max(...lats)]],{padding:70,duration:850,maxZoom:10});
                return;
            }
            const lat=Number(item?.lat),lon=Number(item?.lon);
            if(Number.isFinite(lat)&&Number.isFinite(lon)){
                map.flyTo({center:[lon,lat],zoom:Math.max(8,map.getZoom()),duration:850});
            }
        },
    });
    if(panel.hidden)workspacePanelApi.releaseStationParameter();
    const workspaceState=window.WSWorkspace?.getSnapshot?.()||{};
    const rememberedSection=workspaceState.section;
    const expertOpen=workspaceState.extensions?.interface?.expertOpen===true;
    workspacePanelApi.selectSection(
        expertOpen && rememberedSection && rememberedSection!=='overview' && rememberedSection!=='summary'
            ? rememberedSection
            : 'summary',
    );
    panel.querySelector('#wsi-close').onclick=()=>togglePanel(false);
    panel.querySelector('#wsi-near-me').onclick=locateBaric;
    panel.querySelector('#wsi-sicily').onclick=()=>map.fitBounds([[11.8,35.4],[16.3,38.8]],{padding:35,duration:900});
    panel.querySelector('#wsi-europe').onclick=()=>map.fitBounds([[-25,20],[45,70]],{padding:35,duration:900});
    panel.querySelector('#wsi-clean').onclick=()=>applyCleanView();
    const shadeSelect=panel.querySelector('#wsi-shade-mode'),satelliteToggle=panel.querySelector('#wsi-satellite'),t850Toggle=panel.querySelector('#wsi-t850');
    panel.querySelector('.wsi-pressure-legend').hidden=shadeMode==='off';
    const setSatellite=on=>{const x=document.getElementById('chkSatellite');satelliteToggle.checked=on;if(x&&x.checked!==on){x.checked=on;x.dispatchEvent(new Event('change',{bubbles:true}));}};
    shadeSelect.onchange=e=>{
        shadeMode=e.target.value;t850Toggle.checked=shadeMode==='t850';
        setImageVisible(IDS.pressure,shadeMode!=='off');
        if(shadeMode!=='off'){panel.querySelector('.wsi-pressure-legend').hidden=false;if(currentGrid)renderPressureGrid(currentGrid);setSatellite(false);}
        else panel.querySelector('.wsi-pressure-legend').hidden=true;
    };
    satelliteToggle.onchange=e=>{
        setSatellite(e.target.checked);
        if(e.target.checked){shadeSelect.value='off';shadeMode='off';setImageVisible(IDS.pressure,false);panel.querySelector('.wsi-pressure-legend').hidden=true;t850Toggle.checked=false;}
    };
    t850Toggle.onchange=e=>{
        if(e.target.checked){shadeSelect.value='t850';shadeMode='t850';setImageVisible(IDS.pressure,true);panel.querySelector('.wsi-pressure-legend').hidden=false;if(currentGrid)renderPressureGrid(currentGrid);setSatellite(false);}
        else if(shadeMode==='t850'){shadeSelect.value='off';shadeMode='off';setImageVisible(IDS.pressure,false);panel.querySelector('.wsi-pressure-legend').hidden=true;}
    };
    panel.querySelector('#wsi-isobar').onchange=e=>{visible.isobars=e.target.checked;setIsobarsVisible(visible.isobars);};
    panel.querySelector('#wsi-centers').onchange=e=>{visible.centers=e.target.checked;featureMarkers.forEach(({marker})=>marker.getElement().hidden=!visible.centers);};
    panel.querySelector('#wsi-tracks').onchange=e=>{visible.tracks=e.target.checked;setTracksVisible(visible.tracks);};
    panel.querySelector('#wsi-wind').onchange=e=>{visible.wind=e.target.checked;setWindAnimated(visible.wind);};
    panel.querySelector('#wsi-advanced').onchange=e=>{visible.advanced=e.target.checked;setAdvancedVisible(visible.advanced);};
    panel.querySelector('#wsi-territorial').onchange=e=>{visible.territorial=e.target.checked;setTerritorialVisible(visible.territorial);};
    panel.querySelector('#wsi-physics-select').onchange=()=>loadPhysics(stepIndex).catch(err=>console.warn('[WS Monitor physics]',err));
    panel.querySelector('#wsi-insights').onchange=e=>{
        visible.insights=e.target.checked;
        const frame=scenarioFrameForTime(state,selectedScenarioTimeMs);setLiveInsightVisibility(currentScenarioFrame(frame));
    };
    panel.querySelectorAll('.wsi-modes button').forEach(button=>button.onclick=()=>activateMode(button.dataset.mode));
    const timeSlider=panel.querySelector('#wsi-time');
    timeSlider.oninput=e=>{stepIndex=Number(e.target.value);moveFeatures(stepIndex);};
    timeSlider.onchange=e=>{
        stepIndex=Number(e.target.value);moveFeatures(stepIndex);
        const T=Date.parse(state?.overlays?.timesteps?.[stepIndex]?.valid_utc||'');
        if(Number.isFinite(T))document.dispatchEvent(new CustomEvent('wsv2:intelligence-time-request',{detail:{T}}));
    };
    panel.querySelectorAll('.wsi-fact').forEach(b=>b.onclick=()=>{const lat=Number(b.dataset.lat),lon=Number(b.dataset.lon);if(Number.isFinite(lat)&&Number.isFinite(lon)){map.flyTo({center:[lon,lat],zoom:10});}});
    showIntelligenceAtTime(initialTarget);
}

function setToggle(id,on) {
    const el=document.getElementById(id);if(!el||el.checked===on)return;
    el.checked=on;el.dispatchEvent(new Event('change',{bubbles:true}));
}

function setPanelToggle(id,on) {
    const el=panel?.querySelector('#'+id);if(!el||el.checked===on)return;
    el.checked=on;el.dispatchEvent(new Event('change',{bubbles:true}));
}

function updateModeButtons() {
    panel?.querySelectorAll('.wsi-modes button').forEach(button=>button.classList.toggle('active',button.dataset.mode===uiMode));
}

function activateMode(mode) {
    uiMode=mode;
    const shade=panel?.querySelector('#wsi-shade-mode');
    if(mode==='wind'){
        applyCleanView();
    }else if(mode==='pressure'){
        setPanelToggle('wsi-satellite',false);setPanelToggle('wsi-isobar',true);setPanelToggle('wsi-wind',true);
        setPanelToggle('wsi-territorial',false);setPanelToggle('wsi-insights',false);
        if(shade){shade.value='pressure';shade.dispatchEvent(new Event('change',{bubbles:true}));}
    }else if(mode==='satellite'){
        setPanelToggle('wsi-satellite',true);setPanelToggle('wsi-wind',false);setPanelToggle('wsi-isobar',false);
        setPanelToggle('wsi-territorial',false);setPanelToggle('wsi-insights',false);
    }else if(mode==='events'){
        setPanelToggle('wsi-satellite',false);setPanelToggle('wsi-wind',false);setPanelToggle('wsi-isobar',false);
        setPanelToggle('wsi-territorial',true);setPanelToggle('wsi-insights',true);
        if(shade){shade.value='off';shade.dispatchEvent(new Event('change',{bubbles:true}));}
    }
    updateModeButtons();
}

function applyCleanView() {
    uiMode='wind';
    shadeMode='off';
    const shade=panel?.querySelector('#wsi-shade-mode');if(shade)shade.value='off';
    const t850=panel?.querySelector('#wsi-t850');if(t850)t850.checked=false;
    setImageVisible(IDS.pressure,false);
    const legend=panel?.querySelector('.wsi-pressure-legend');if(legend)legend.hidden=true;
    const defaults={isobar:false,wind:true,advanced:false,territorial:false,centers:true,tracks:false,insights:false};
    for(const [name,on] of Object.entries(defaults)){
        const el=panel?.querySelector('#wsi-'+name);if(el){el.checked=on;el.dispatchEvent(new Event('change',{bubbles:true}));}
    }
    // La sinottica nasce pulita. Stazioni e campi puntuali restano riattivabili
    // dal pannello Layers, ma non coprono piu' vento e isobare all'apertura.
    setToggle('chkMarkers',false);setToggle('chkInterpol',false);
    updateModeButtons();
}

function coordinateLegacySurfaces(show) {
    const layersPanel = document.getElementById('layersPanel');
    if (show && layersPanel?.classList.contains('open')) document.getElementById('btnLayersClose')?.click();
    const viewButton = document.getElementById('btn-view-options');
    if (show && viewButton?.getAttribute('aria-expanded') === 'true') viewButton.click();
    const parameterDock = document.getElementById('wsv2-view-data');
    const parameterButton = document.getElementById('btn-param-dock-toggle');
    if (show) {
        legacyParamDockWasCollapsed = parameterDock?.classList.contains('is-collapsed') ?? null;
        if (legacyParamDockWasCollapsed === false) parameterButton?.click();
    } else if (legacyParamDockWasCollapsed === false && parameterDock?.classList.contains('is-collapsed')) {
        parameterButton?.click();
        legacyParamDockWasCollapsed = null;
    }
}

function togglePanel(show) {
    coordinateLegacySurfaces(show);
    if(show)workspacePanelApi?.claimStationParameter?.();
    else{
        workspacePanelApi?.stopEvolution?.();
        workspacePanelApi?.releaseStationParameter?.();
    }
    panel.hidden=!show;
    panel.setAttribute('aria-hidden',String(!show));
    if (launchButton) {
        launchButton.hidden=show;
        launchButton.setAttribute('aria-expanded',String(show));
    }
    document.documentElement.classList.toggle('wsi-panel-open',show);
}

async function load() {
    const [response,v3Result]=await Promise.all([
        fetch(STATE_URL,{cache:'no-store',credentials:'same-origin'}),
        fetch(WSNOW_V3_URL,{cache:'no-store',credentials:'same-origin'})
            .then(async response=>response.ok?response.json():null)
            .catch(()=>null),
    ]);
    if(!response.ok)throw new Error(`stato Intelligence HTTP ${response.status}`);
    publicationCycle=response.headers.get('X-WS-Cycle-ID')||'';
    if(location.hostname.startsWith('intelligence.')&&!publicationCycle){
        throw new Error('stato Intelligence privo di identità di ciclo');
    }
    state=await response.json();
    syncWorkspaceContract();
    wsnowV3=v3Result?.ok===true?v3Result:null;
    territorialGeoJSON=null;territorialDocument=null;
    addInsightMarkers();renderPanel();
    setAdvancedVisible(visible.advanced);setIsobarsVisible(visible.isobars);setTracksVisible(visible.tracks);
    loadTerritorial().then(()=>{
        const frame=scenarioFrameForTime(state,selectedScenarioTimeMs);
        if(frame){
            const territorialCount=updateTerritorialScenario(frame.valid_ms);
            updateScenarioNarrative(frame,currentScenarioFrame(frame),territorialCount);
            updateIntelligenceWorkspaceContext({
                state,
                mode:workspaceTemporalMode(selectedScenarioTimeMs ?? frame.valid_ms),
                targetMs:selectedScenarioTimeMs ?? frame.valid_ms,
                territorialCount,
                territorialDocument,
            });
        }
    }).catch(err=>console.warn('[WS Monitor territoriale]',err));
}

export function initIntelligence(mapInstance) {
    if (!activeHere()) return;
    map=mapInstance;
    document.documentElement.classList.add('wsi-product');
    panel=document.createElement('aside'); panel.className='wsi-panel'; panel.id='wsi-panel'; document.body.appendChild(panel);
    launchButton=document.createElement('button');
    launchButton.className='wsi-reopen'; launchButton.id='btn-wsi'; launchButton.type='button';
    launchButton.title='Apri il briefing Weather Sicily'; launchButton.setAttribute('aria-label','Apri il briefing Weather Sicily');
    launchButton.innerHTML='<span aria-hidden="true">✦</span><b>Briefing WS</b>';
    launchButton.hidden=true; document.body.appendChild(launchButton);
    launchButton.onclick=()=>togglePanel(true);
    document.getElementById('btnLayers')?.addEventListener('click',()=>{if(!panel.hidden)togglePanel(false);});
    load().then(()=>{togglePanel(true);setTimeout(()=>{const sat=document.getElementById('chkSatellite');if(sat?.checked){sat.checked=false;sat.dispatchEvent(new Event('change',{bubbles:true}));}if(!cleanDefaultsApplied){cleanDefaultsApplied=true;applyCleanView();}},350);}).catch(err=>{console.error('[WS Monitor]',err);panel.innerHTML='<p>WS Monitor temporaneamente non disponibile.</p>';});
    setInterval(()=>load().catch(err=>console.warn('[WS Monitor refresh]',err)),REFRESH_MS);
}
